import React from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerPriceList } from '../../services/types';

interface PartCustomerPriceTableProps {
  customerPriceList: CustomerPriceList;
}

export const PartCustomerPriceTable: React.FC<PartCustomerPriceTableProps> = ({
  customerPriceList,
}) => {
  const { t } = useTranslation();

  const { priceListCodes, priceBreaks } = customerPriceList;

  /**
   * Get currency symbol based on price list code
   */
  const getCurrencySymbol = (priceListCode: string): string => {
    if (priceListCode.includes('EUR')) {
      return '€';
    }
    return '$';
  };

  /**
   * Format currency value with proper locale handling
   */
  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /**
   * Format quantity as a range bracket
   * Each quantity represents the START of that price tier
   * - Single break: "1+" (all quantities)
   * - Multiple breaks: "1-3", "4-7", "8+" style ranges
   */
  const formatQuantityRange = (
    currentQuantity: number,
    index: number,
    totalBreaks: number
  ): string => {
    // Special case: single break (all quantities use this price)
    if (totalBreaks === 1) {
      return `${currentQuantity.toLocaleString()}+`;
    }

    // Last bracket: "X+" (from this quantity onwards)
    if (index === totalBreaks - 1) {
      return `${currentQuantity.toLocaleString()}+`;
    }

    // All other brackets: "X-Y" (from current to next-1)
    const nextQuantity = priceBreaks[index + 1].quantity;
    return `${currentQuantity.toLocaleString()}-${(nextQuantity - 1).toLocaleString()}`;
  };

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header */}
      <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200 flex-shrink-0">
        <h3 className="text-lg font-bold text-indigo-900">
          {t('parts.customerPriceList')}
        </h3>
        <p className="text-xs text-indigo-700">
          {priceListCodes.length}{' '}
          {priceListCodes.length === 1
            ? t('parts.priceList')
            : t('parts.priceListPlural')}{' '}
          • {priceBreaks.length}{' '}
          {priceBreaks.length === 1
            ? t('parts.quantityBreak')
            : t('parts.quantityBreakPlural')}
        </p>
      </div>

      {/* Content */}
      {priceBreaks.length > 0 && priceListCodes.length > 0 ? (
        <div className="overflow-x-auto flex-grow">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">
                  {t('parts.customerPriceTable.quantity')}
                </th>
                {priceListCodes.map((priceListCode) => (
                  <th
                    key={priceListCode}
                    className="px-4 py-3 text-right font-semibold text-gray-700 whitespace-nowrap"
                  >
                    {priceListCode}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {priceBreaks.map((priceBreak, idx) => (
                <tr
                  key={idx}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 text-gray-900 font-semibold">
                    {formatQuantityRange(
                      priceBreak.quantity,
                      idx,
                      priceBreaks.length
                    )}
                  </td>
                  {priceListCodes.map((priceListCode) => {
                    const price = priceBreak.prices[priceListCode];
                    const currencySymbol = getCurrencySymbol(priceListCode);
                    return (
                      <td
                        key={priceListCode}
                        className="px-4 py-3 text-right text-gray-900 font-medium"
                      >
                        {price !== undefined && price !== null
                          ? `${currencySymbol}${formatCurrency(price)}`
                          : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4">
          <p className="text-sm font-medium text-gray-500">
            {t('parts.noPricingData')}
          </p>
        </div>
      )}
    </div>
  );
};
